/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import React from 'react';

import { Icon, Label, Step, StepGroup } from '@mairistem/composants';

import { Steps } from '@/enums';
import type { AssistantStep } from '@/types';
import { COLORS } from '@/constants';
import { AssistantContext } from '@/contexts';
import { hasRepresentantsLegaux } from '@/utils';

// Style pour la pastille valide / invalide pour ne pas dépasser sur
// le titre de la Step
const STEP_BADGE_STYLE: React.CSSProperties = {
  position: 'absolute',
  bottom: -3,
  right: -3,
  margin: 0,
  width: 16,
  height: 16,
  fontSize: 12,
  lineHeight: '16px',
  textAlign: 'center',
  backgroundColor: 'white',
  borderRadius: '50%',
  pointerEvents: 'none',
};

interface Props {
  /**
   * Index de l'étape actuelle
   * 0 : Identification
   * 1 : Adresses & contacts
   * 2 : Filiation
   * 3 : Informations complémentaires
   */
  currentStepIndex: number;
  /** Toutes les étapes de l'assistant */
  steps: Partial<Record<Steps, AssistantStep>>;
  /** Callback appelé lors du changement d'étape */
  onStepChange: (stepIndex: Steps) => void;
}

const AssistantHeader = (props: Props) => {
  // #region Declarations
  const { currentStepIndex, steps, onStepChange } = props;

  const pacs = React.useContext(AssistantContext);
  const {
    errors,
    selectedDeclarationType,
    selectedPacs,
    conventionDeclaration,
  } = pacs || {};

  // Y compris en mode Hubee : les protections juridique/OFPRA préremplies
  // depuis la pré-demande font des parents les représentants légaux.
  const representants1 = hasRepresentantsLegaux(
    conventionDeclaration?.partenaire1,
  );
  const representants2 = hasRepresentantsLegaux(
    conventionDeclaration?.partenaire2,
  );
  const stepIndexes = Object.keys(steps).map(
    (stepKey) => Number(stepKey) as Steps,
  );
  const lastStepIndex = _.last(stepIndexes);

  // #endregion

  // #region Component

  return (
    <StepGroup
      fluid
      className="connected"
      style={{
        borderTop: 'none',
        borderLeft: 'none',
        borderRight: 'none',
        minHeight: '9em',
      }}
    >
      {_.map(steps, (step: AssistantStep, index: Steps) => {
        const stepPosition = Object.keys(steps).indexOf(String(index)) + 1;
        const { title, dataTestId, validated = false } = step;

        const stepIndex: Steps = Number(index);
        let displayedTitle = title;
        if (stepIndex === lastStepIndex && stepIndex === Steps.PACS) {
          displayedTitle = 'Déclaration';
        } else if (
          stepIndex === Steps.PACS &&
          selectedDeclarationType === 'projet'
        ) {
          // Projet : pas encore un PACS, comme « Sélection du projet »
          displayedTitle = 'Projet';
        } else if (
          stepIndex === Steps.TypeDeclaration &&
          selectedDeclarationType
        ) {
          // On affiche le type sélectionné dans le libellé de l'étape
          displayedTitle = `${title} (${selectedDeclarationType})`;
        } else if (stepIndex === Steps.SelectionPACS) {
          displayedTitle =
            selectedDeclarationType === 'projet' ||
            selectedDeclarationType === 'initiale'
              ? 'Sélection du projet'
              : 'Sélection du PACS';
        } else if (stepIndex === Steps.ParentsPartenaire1 && representants1) {
          displayedTitle = 'Représentants légaux 1';
        } else if (stepIndex === Steps.ParentsPartenaire2 && representants2) {
          displayedTitle = 'Représentants légaux 2';
        }
        const errorsStep = errors?.[stepIndex];

        // Vérification des étapes obligatoires non remplies qui empêchent la navigation
        const isStep1Missing = !selectedDeclarationType;
        const isStep2Missing = !selectedPacs;

        // Une étape ultérieure est désactivée s'il manque une donnée requise à une étape précédente
        // On bloque l'accès aux étapes suivantes tant que pas de typeDeclaration sélectionné
        // et de projet sélectionné
        const isDisabled =
          (stepIndex > Steps.TypeDeclaration && isStep1Missing) ||
          (stepIndex > Steps.SelectionPACS && isStep2Missing);

        // TODO : lors de la gestion des erreurs, adapter cette partie de code
        // if (!_.isEmpty(errorsStep)) {
        //   content = (
        //     <Icon
        //       fitted
        //       color="red"
        //       size="big"
        //       name="times circle"
        //     />
        //   );
        // }

        const content: React.ReactNode = (
          <Label
            circular
            size="big"
            style={{
              position: 'relative',
              backgroundColor:
                stepIndex <= currentStepIndex ? COLORS.Background : 'white',
              border: `solid 1px ${COLORS.HeaderStepBorder}`,
              color:
                stepIndex <= currentStepIndex ? 'white' : COLORS.Background,
            }}
          >
            {stepPosition}
            {(validated || stepIndex < currentStepIndex) &&
              _.isEmpty(errorsStep) && (
                <Icon
                  name="checkmark"
                  style={{ ...STEP_BADGE_STYLE, color: 'green' }}
                />
              )}
            {!_.isEmpty(errorsStep) && stepIndex < currentStepIndex && (
              <Icon
                name="close"
                style={{ ...STEP_BADGE_STYLE, color: 'red' }}
              />
            )}
          </Label>
        );

        return (
          <Step
            key={title}
            style={{
              backgroundColor: 'transparent',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
            }}
            data-testid={dataTestId}
            onClick={() => !isDisabled && onStepChange(stepIndex)}
          >
            <Step.Content style={{ bottom: '0.6em' }}>
              {content}
              <Step.Title style={{ paddingTop: 5, color: COLORS.Background }}>
                {displayedTitle}
              </Step.Title>
            </Step.Content>
          </Step>
        );
      })}
    </StepGroup>
  );
};

export default AssistantHeader;
